import PropTypes from "prop-types";
import { useDCContext } from "@src/context";
import { useOperatorContext } from "@src/context/operatorContext";
import { COMMON_CONTROLS } from "@src/stories/utils";
import { AppUtils } from "@src/utils";
import { getNumberFormatterForUnit } from "../utility";

/**
 * Display a charity donation amount including units. Units are derived from Dynamic Content.
 * @returns {string}
 */
const DonationAmount = ({
  industry,
  customerCode,
  amount,
  preferUnitsOfMeasureFormatters = false,
}) => {
  const { getDynamicReplacement, existsDynamicReplacements } = useDCContext();

  /** @returns {string} */
  const formatDonation = () => {
    //Unit (USD, kg, etc.) is currently only available in Dynamic Content
    const unitDcKey = existsDynamicReplacements(
      `charities__${customerCode.toLowerCase()}__donation_currency`,
    )
      ? `charities__${customerCode.toLowerCase()}__donation_currency`
      : `charities__default__donation_currency`;

    if (preferUnitsOfMeasureFormatters) {
      //Units of measure formatting is NOT preferred right now (eg. "0.007 MT plastic removed" isn't great)
      //HACKY: grab the charity unit via DC, ignoring where the AMOUNT is in the string
      const unit = getDynamicReplacement(
        unitDcKey,
        {
          AMOUNT: "{{AMOUNT}}",
        },
        "USD",
        true,
      )
        .toUpperCase()
        .replace("{{AMOUNT}}", "")
        .trim();
      const formatterFunction = getNumberFormatterForUnit(unit, industry);
      return formatterFunction(amount).formattedValue;
    }
    //Prefer DC for formatting
    return getDynamicReplacement(
      unitDcKey,
      {
        AMOUNT: amount,
      },
      amount,
      true,
    );
  };

  return formatDonation();
};

export const DonationAmountOperator = ({ amount = 0 }) => {
  const { profile } = useOperatorContext();
  if (!profile.customer?.customerCode) return null;

  const { industry, customerCode } = profile.customer;
  return DonationAmount({ amount, industry, customerCode });
};

export const donationAmountControls = {
  industry: COMMON_CONTROLS.industry,
  customerCode: COMMON_CONTROLS.customerCode,
  amount: {
    propType: PropTypes.number.isRequired,
    controlType: "number",
  },
};
DonationAmount.propTypes = AppUtils.getPropTypeFromControls(
  donationAmountControls,
);

export default DonationAmount;
